import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useState } from 'react'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { ApiError } from '@/util/errors/api-error'
import { loadUserFileMarkdownHtml, loadUserFilePdf, loadUserFileViewableContent } from '../utils/load-user-file-viewable-content'

type UseUserFileViewableContentOptions = {
  userFileId: string
  // Omit format to resolve PDF vs markdown from the viewable-url response at load time.
  format?: RenderFormat.PDF | RenderFormat.MARKDOWN
  title?: string
  citationHighlightsImprovements: boolean
}

// 'unsupported' is terminal: the file has no viewable representation (e.g. an image), so a
// retry can never succeed. 'transient' covers everything a retry might fix.
export type UserFileViewableContentError = 'unsupported' | 'transient'

type UseUserFileViewableContentReturn = {
  loading: boolean
  error: UserFileViewableContentError | null
  /** The file no longer exists (or is no longer reachable by this member), so retrying can't help. */
  deleted: boolean
  pdfFile: File | null
  htmlString: string | null
  retry: () => void
}

export function useUserFileViewableContent(options: UseUserFileViewableContentOptions): UseUserFileViewableContentReturn {
  const { userFileId, format, title, citationHighlightsImprovements } = options
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<UserFileViewableContentError | null>(null)
  const [deleted, setDeleted] = useState(false)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [htmlString, setHtmlString] = useState<string | null>(null)
  const [refetchCount, setRefetchCount] = useState(0)

  const retry = useCallback(() => {
    setRefetchCount((previous) => previous + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadContent() {
      setLoading(true)
      setError(null)
      setDeleted(false)
      setPdfFile(null)
      setHtmlString(null)

      try {
        if (format === RenderFormat.PDF) {
          const nextPdfFile = await loadUserFilePdf({ userFileId, title })
          if (!cancelled) setPdfFile(nextPdfFile)
          return
        }

        if (format === RenderFormat.MARKDOWN) {
          const nextHtmlString = await loadUserFileMarkdownHtml({ userFileId, citationHighlightsImprovements })
          if (!cancelled) setHtmlString(nextHtmlString)
          return
        }

        const content = await loadUserFileViewableContent({ userFileId, title, citationHighlightsImprovements })
        if (cancelled) return
        if (content.kind === 'pdf') {
          setPdfFile(content.pdfFile)
        } else {
          setHtmlString(content.htmlString)
        }
      } catch (e) {
        if (cancelled) return

        // The viewable-url endpoint 404s a file that is gone or no longer reachable by this member.
        // That is an expected outcome for a reference to a deleted file, not a load failure, so it
        // is deliberately not reported: the caller renders the deleted state instead of a retry the
        // user can never satisfy, the same way a 403/404 folder-files fetch quietly clears the view.
        if (e instanceof ApiError && e.status === 404) {
          setDeleted(true)
          return
        }

        // The viewable-url endpoint 409s when the file has no viewable representation. For a
        // completed file that is a lasting state (e.g. images have none), not an exception.
        if (e instanceof ApiError && e.status === 409) {
          setError('unsupported')
          return
        }

        Sentry.captureException(e, {
          extra: {
            userFileId,
            format,
            title,
          },
        })
        setError('transient')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadContent()

    return () => {
      cancelled = true
    }
  }, [citationHighlightsImprovements, format, refetchCount, title, userFileId])

  return { loading, error, deleted, pdfFile, htmlString, retry }
}
